import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { useTranslation } from 'react-i18next';

import { useConfirmDeviceMutation } from '../../../queries/devices';
import { useGetLocations } from '../../../queries/locations';

import { useDispatch, useSelector } from 'react-redux';
import { toggleDialog } from '../../../redux/Slices/Global/globalSlice';

// Screen + pairing badge — same stroke-icon family as the other dialogs
// (.claude/pp-dialogs-prototype.html lists "اقتران" under the form variant).
const IcPair = () => (
    <svg viewBox="0 0 24 24">
        <rect x="3" y="4" width="15" height="11" rx="2" /><path d="M8 19h6" />
        <path d="M18 3v6M15 6h6" />
    </svg>
);
const IcClose = () => <svg width="18" height="18" viewBox="0 0 24 24" style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 2 }}><path d="M6 6l12 12M18 6L6 18" /></svg>;

function Confirm() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const openDialog = useSelector((state) => state.global.openDialog);

    const { register, formState: { errors }, handleSubmit } = useForm();

    const confirmDevice = useConfirmDeviceMutation();

    // Branch/Location the device belongs to — drives its scheduling timezone.
    const locationsRaw = useGetLocations().data;
    const locations = Array.isArray(locationsRaw) ? locationsRaw : (locationsRaw?.data ?? []);
    const [locationId, setLocationId] = useState(null);
    const locationOptions = locations.map((l) => ({
        label: l.timezone ? `${l.name} · ${l.timezone}` : l.name,
        value: l.id,
    }));

    const onSubmit = async (data) => {
        await confirmDevice.mutateAsync({
            "code": data.code.replace(/\s/g, ''),
            "name": data.name,
            "location_id": locationId,
        })
    };

    const handleFocus = (e) => {
        var val = e.target.value;
        e.target.value = '';
        e.target.value = val;
    }

    const onClose = () => dispatch(toggleDialog("confirmDialog"));

    return (
        <>
            <Dialog visible={openDialog.confirmDialog}
                style={{ width: '420px' }}
                breakpoints={{ '960px': '95vw' }}
                showHeader={false}
                modal
                className="pp-dlg"
                onHide={onClose}
            >
                <div className="pp-dlg-hd">
                    <span className="ic"><IcPair /></span>
                    <div>
                        <div className="t">{t('devices.group_new_device')}</div>
                    </div>
                    <span className="x" onClick={onClose}><IcClose /></span>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleSubmit(onSubmit)();
                        }
                    }}>
                    <div className="pp-dlg-bd">
                        <div className="pp-dlg-fld">
                            <label>{t('devices.group_device_name_label')}</label>
                            <InputText
                                {...register("name", { required: t('devices.group_device_name_required') })}
                                aria-invalid={errors.name ? "true" : "false"}
                            />
                            {errors.name && <small className="field-error">{errors.name?.message}</small>}
                        </div>

                        {locationOptions.length > 0 && (
                            <div className="pp-dlg-fld">
                                <label>{t('devices.location_label')}</label>
                                <Dropdown
                                    value={locationId}
                                    options={locationOptions}
                                    onChange={(e) => setLocationId(e.value)}
                                    placeholder={t('devices.location_placeholder')}
                                    showClear
                                    filter
                                />
                                <small className="text-muted mt-1">{t('devices.location_hint')}</small>
                            </div>
                        )}

                        <div className="pp-dlg-fld">
                            <label>{t('devices.group_pairing_code_label')}</label>
                            <InputMask
                                dir='ltr'
                                {...register("code", { required: t('devices.group_device_code_required') })}
                                aria-invalid={errors.code ? "true" : "false"}
                                className="text-center device-code-input"
                                id='basic'
                                mask="9 9 9 9 9 9"
                                placeholder="9 9 9 9 9 9"
                                style={{ height: "clamp(60px, 15vw, 100px)", fontSize: "clamp(28px, 8vw, 50px)", letterSpacing: "2px" }}
                                onFocus={handleFocus}
                            ></InputMask>
                            {errors.code && <small className="field-error">{errors.code?.message}</small>}
                        </div>
                    </div>

                    <div className="pp-dlg-ft">
                        <button type="submit" className="btn primary" disabled={confirmDevice.isLoading}>{t('buttons.confirm')}</button>
                        <button type="button" className="btn ghost" disabled={confirmDevice.isLoading} onClick={onClose}>{t('buttons.cancel')}</button>
                    </div>
                </form>
            </Dialog>
        </>
    )
}

export default Confirm
